import { LBSubscriptionStatus } from "@/services/LBSubscriptionService";

interface PrivacyDetailsTabProps {
    subscriptionStatus: LBSubscriptionStatus;
}

export const PrivacyDetailsTab: React.FC<PrivacyDetailsTabProps> = ({ subscriptionStatus }) => {
    return (
        <div className="space-y-4">
            <h3 className="text-base font-medium text-gray-900">Privacy settings</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {/* LB Visible Badge */}
                <div className="flex items-center">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary text-white">
                        {subscriptionStatus.lbVisible ? (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                            </svg>
                        ) : (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                                />
                            </svg>
                        )}
                        {subscriptionStatus.lbVisible ? "LB Visible" : "LB Hidden"}
                    </span>
                </div>

                {/* Live Scope Badge */}
                <div className="flex items-center">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-500 text-white">
                        {subscriptionStatus.liveScope === "everyone" ? (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                />
                            </svg>
                        ) : subscriptionStatus.liveScope === "nobody" ? (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                                />
                            </svg>
                        ) : (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                            </svg>
                        )}
                        Live: {subscriptionStatus.liveScope || "followers"}
                    </span>
                </div>

                {/* Private/Public Badge */}
                <div className="flex items-center">
                    <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            subscriptionStatus.privateAccount
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-green-100 text-green-800"
                        }`}
                    >
                        {subscriptionStatus.privateAccount ? (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        ) : (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" />
                            </svg>
                        )}
                        {subscriptionStatus.privateAccount ? "Private" : "Public"}
                    </span>
                </div>
            </div>

            {/* Explanatory Text */}
            <div className="mt-6 space-y-2 text-sm text-gray-600">
                <p>
                    <span className="font-medium">LB Visible:</span>{" "}
                    {subscriptionStatus.lbVisible
                        ? "User's profile is visible on the leaderboard."
                        : "User's profile is hidden from the leaderboard."}
                </p>
                <p>
                    <span className="font-medium">Live Scope:</span>{" "}
                    {subscriptionStatus.liveScope === "everyone"
                        ? "Live sessions are visible to everyone."
                        : subscriptionStatus.liveScope === "nobody"
                        ? "Live sessions are not visible to anyone."
                        : "Live sessions are visible to followers only."}
                </p>
                <p>
                    <span className="font-medium">Account Type:</span>{" "}
                    {subscriptionStatus.privateAccount
                        ? "Private account - only approved followers can see content."
                        : "Public account - content is visible to all users."}
                </p>
            </div>
        </div>
    );
};
